import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { evaluateSmartCreationPlanStructures } from "@/lib/smart-creation-api";
import { distinctParentCount, structureEvaluation } from "./draft-rules";
import { structureEvaluationQueryKey } from "./plan-queries";
import type { PlanSession } from "./use-plan-session";

interface StructureEvaluationOptions {
  session: PlanSession;
  open: boolean;
  planId: string;
  canEdit: boolean;
}

/** Evaluates every structure option for the current Plan revision while on the Structure step. */
export function useStructureEvaluation({
  session,
  open,
  planId,
  canEdit,
}: StructureEvaluationOptions) {
  const { t } = useTranslation("smartCreation");
  const { plan, planShopId, currentStep, displayedDraft } = session;
  const structureEvaluationQuery = useQuery({
    queryKey: structureEvaluationQueryKey(planShopId, plan?.id ?? planId, plan?.revision ?? 0),
    // Retain this plan's diagram during a revision refresh, never another shop's evaluation.
    placeholderData: (previous, previousQuery) =>
      previousQuery?.queryKey[2] === planShopId &&
      previousQuery.queryKey[3] === (plan?.id ?? planId)
        ? previous
        : undefined,
    queryFn: () => {
      if (!plan) throw new Error(t("errors.load"));
      return evaluateSmartCreationPlanStructures(plan.id, plan.shopId, plan.revision);
    },
    enabled:
      open &&
      canEdit &&
      (plan?.status === "draft" || plan?.status === "preview_ready") &&
      currentStep === "structure",
    retry: false,
    staleTime: 60_000,
  });
  const selectedStructureEvaluation = displayedDraft
    ? structureEvaluation(structureEvaluationQuery.data, displayedDraft.structure)
    : undefined;
  const structureEvaluationBlocksContinue =
    canEdit &&
    currentStep === "structure" &&
    (structureEvaluationQuery.isFetching ||
      structureEvaluationQuery.isError ||
      (displayedDraft?.structure === "precise" && distinctParentCount(displayedDraft) > 1) ||
      !selectedStructureEvaluation?.feasible);

  // A failed evaluation may mean another tab moved the Plan revision; surface that as a conflict.
  const { browserDraftRef, revisionConflictRef, handleRevisionConflict } = session;
  const error = structureEvaluationQuery.error;
  useEffect(() => {
    const currentPlan = browserDraftRef.current;
    if (!error || !currentPlan || revisionConflictRef.current) return;
    void handleRevisionConflict(error, {
      identity: `${currentPlan.shopId}:${currentPlan.id}`,
      planId: currentPlan.id,
      shopId: currentPlan.shopId,
    });
  }, [browserDraftRef, handleRevisionConflict, revisionConflictRef, error]);

  return {
    structureEvaluationQuery,
    selectedStructureEvaluation,
    structureEvaluationBlocksContinue,
  };
}
